import { useState, useRef, useEffect, useCallback } from "react";
import {
  ContentLayout,
  Header,
  Container,
  Box,
  Button,
  SpaceBetween,
  Select,
  FormField,
  Spinner,
  ExpandableSection,
  TextContent,
  Textarea,
  CopyToClipboard,
  Checkbox,
  Modal,
  StatusIndicator,
} from "@cloudscape-design/components";
import { boxerService } from "../../services/boxerService";
import { useAuth } from "../../context/AuthContext";
import { useChatSession } from "../../context/ChatSessionContext";
import DataTable from "../../components/common/DataTable";
import MarkdownRenderer from "../../components/common/MarkdownRenderer";
import ModeSwitcher from "../../components/common/Modeswitcher";

const QUERY_MODES = [
  { label: "OpsLense",  value: "schema",        description: "Query live metadata & structured data tables" },
  { label: "DocuSense", value: "knowledge_base", description: "Search documents, SOPs, policies & guides"   },
];

const MODEL_OPTIONS = [
  { label: "Claude Sonnet", value: "claude_sonnet" },
  { label: "Claude Haiku",  value: "claude_haiku"  },
  { label: "Nova Pro",      value: "nova"           },
];

const THUMBS_DOWN_REASONS = [
  "Incorrect information",
  "Incomplete answer",
  "Query failed or errored",
  "Results don't match my question",
  "Too verbose or unclear",
  "Other",
];

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return "🌅 Good Morning";
  if (h >= 12 && h < 17) return "☀️ Good Afternoon";
  if (h >= 17 && h < 21) return "🌇 Good Evening";
  return "🌙 Good Night";
}

if (typeof document !== "undefined" && !document.getElementById("blink-style")) {
  const style = document.createElement("style");
  style.id = "blink-style";
  style.textContent = [
    "@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }",
    "@keyframes micPulse { 0%,100%{transform:scale(1);opacity:0.6} 50%{transform:scale(1.5);opacity:0} }",
  ].join(" ");
  document.head.appendChild(style);
}

export default function ExecuteQuery() {
  const { companylogo, user } = useAuth();
  const {
    sessions, currentSessionId, setCurrentSessionId,
    messages, setMessages,
    queryMode, setQueryMode,
    sessionsLoaded, loadSessions,
    startNewChat: ctxStartNewChat,
    addSession, saveMessages,
    createSavedPrompt,
    pendingPromptText, setPendingPromptText,
  } = useChatSession();

  // Chat input & send state
  const [input, setInput]                   = useState("");
  const [loading, setLoading]               = useState(false);
  const [loadingStatus, setLoadingStatus]   = useState(""); // rotating status label shown while AI is thinking
  const [modelKey, setModelKey]             = useState(MODEL_OPTIONS[0]);

  // AI response extras: follow-up suggestions and clarifying questions
  const [suggestions, setSuggestions]       = useState([]);
  const [clarifications, setClarifications] = useState([]);

  // Thumbs-down feedback modal
  const [feedbackModal, setFeedbackModal]     = useState({ visible: false, message: null });
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState("");

  // "Create Reusable Prompt" modal — distils conversation into a saveable prompt template
  const [createPromptModal, setCreatePromptModal] = useState(false);
  const [distillLoading, setDistillLoading]       = useState(false);
  const [distilledPrompt, setDistilledPrompt]     = useState("");
  const [promptName, setPromptName]               = useState("");
  const [savingPrompt, setSavingPrompt]           = useState(false);

  // Voice input (Web Speech API — Chrome/Edge only, hidden on Firefox)
  const [listening, setListening]                 = useState(false);  // mic active
  const [interimText, setInterimText]             = useState("");     // live preview while speaking
  const recognitionRef                            = useRef(null);     // SpeechRecognition instance
  const inputValueRef                             = useRef("");       // ref mirror of input — avoids stale closure inside onresult
  const silenceTimerRef                           = useRef(null);     // auto-stops after 2s of silence

  const queryModeObj = QUERY_MODES.find(m => m.value === queryMode) || QUERY_MODES[0];

  // Scroll anchor and message tracking refs
  const bottomRef          = useRef(null);
  const messagesRef        = useRef(messages);  // ref mirror so sendMessage always reads latest messages
  const prevMsgCount       = useRef(0);
  const prevSessionIdRef   = useRef(currentSessionId);
  const abortControllerRef = useRef(null);  // cancels in-flight stream on unmount or new send

  // Tracks which session the current in-flight stream belongs to.
  // Compared against currentSessionId to decide whether to update UI or only save in background.
  const activeSessionIdRef = useRef(currentSessionId);

  // Tracks the session ID of whichever stream is currently running (used to decide
  // whether to abort when a new send starts — only abort if same session).
  const streamingSessionIdRef = useRef(null);

  messagesRef.current = messages;
  inputValueRef.current = input;

  // Load sessions on mount
  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Abort any in-flight stream when navigating away from this page
  useEffect(() => {
    return () => { abortControllerRef.current?.abort(); };
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      prevMsgCount.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Reset local state when session switches
  useEffect(() => {
    if (prevSessionIdRef.current !== currentSessionId) {
      prevSessionIdRef.current = currentSessionId;
      prevMsgCount.current = 0;
      setSuggestions([]);
      setClarifications([]);
      // Only clear loading when the user navigates to a DIFFERENT session than the one
      // currently streaming. If activeSessionIdRef already matches currentSessionId it means
      // a new session was just created for the active stream — don't kill the spinner.
      if (activeSessionIdRef.current !== currentSessionId) {
        setLoading(false);
        setLoadingStatus("");
      }
    }
    activeSessionIdRef.current = currentSessionId;  // always keep ref in sync
  }, [currentSessionId]);

  // Paste saved prompt into input box when user clicks one in the sidebar
  useEffect(() => {
    if (pendingPromptText) {
      setInput(pendingPromptText);
      setPendingPromptText(null);
    }
  }, [pendingPromptText, setPendingPromptText]);

  const startNewChat = useCallback(() => {
    ctxStartNewChat();
    setSuggestions([]);
    setClarifications([]);
    prevMsgCount.current = 0;
  }, [ctxStartNewChat]);

  const handleModeChange = (mode) => {
    setQueryMode(mode.value);
    ctxStartNewChat();
    setSuggestions([]);
    setClarifications([]);
    prevMsgCount.current = 0;
  };

  const handleModelChange = ({ detail }) => {
    setModelKey(detail.selectedOption);
    ctxStartNewChat();
    setSuggestions([]);
    setClarifications([]);
    prevMsgCount.current = 0;
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    // Only abort if the current in-flight stream belongs to THIS same session.
    // If a background stream (different session) is still running, let it finish
    // so it can save its messages to DynamoDB on completion.
    if (streamingSessionIdRef.current !== null && streamingSessionIdRef.current === currentSessionId) {
      abortControllerRef.current?.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Capture messages before this send — used to reconstruct a complete record for background save
    const initialMessages = messagesRef.current;
    const history = initialMessages.map(m => ({ role: m.role, content: m.content }));

    // Local closure variable — each concurrent sendMessage gets its own accumulator.
    // Using a ref here would be wrong: Chat B's sendMessage would overwrite Chat A's data.
    const bgAccumulator = { content: "", tableData: [], tableColumns: [], rowCount: 0, queryUsed: "", dataSource: "", queryType: "" };

    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setInput("");
    setSuggestions([]);
    setClarifications([]);
    setLoading(true);

    // Create session on first message
    let sessionId = currentSessionId;
    if (!sessionId) {
      try {
        sessionId = await boxerService.createSession(msg.slice(0, 80), queryModeObj.value);
        setCurrentSessionId(sessionId);
        activeSessionIdRef.current = sessionId;  // sync immediately so isStreamActive() is correct before next render
        addSession({
          session_id: sessionId,
          title:      msg.slice(0, 80),
          query_mode: queryModeObj.value,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } catch { /* proceed without saving */ }
    }

    // Register this stream's session so the abort guard can identify it
    streamingSessionIdRef.current = sessionId;

    const statuses = [
      "🔍 Identifying relevant tables...",
      "⚙️ Generating query...",
      "📊 Executing query...",
      "✍️ Summarizing results...",
    ];
    let statusIdx = 0;
    setLoadingStatus(statuses[0]);
    const statusTimer = setInterval(() => {
      statusIdx = Math.min(statusIdx + 1, statuses.length - 1);
      setLoadingStatus(statuses[statusIdx]);
    }, 4000);

    const msgId = `msg_${Date.now()}`;

    // Returns true if the user is still viewing the session this stream was started for.
    // When false, stream events are accumulated silently and saved to DB on completion.
    const isStreamActive = () => activeSessionIdRef.current === sessionId;

    try {
      await boxerService.streamChatMessage(
        msg, history, queryModeObj.value, modelKey.value,
        (event) => {
          if (abortController.signal.aborted) return;

          if (event.event === "table") {
            // Always capture table data in accumulator for background save
            const acc = bgAccumulator;
            acc.tableData    = event.table_data    || [];
            acc.tableColumns = event.table_columns || [];
            acc.rowCount     = event.row_count     || 0;
            acc.queryUsed    = event.query_used    || "";
            acc.dataSource   = event.data_source   || "";
            acc.queryType    = event.query_type    || "";

            if (isStreamActive()) {
              clearInterval(statusTimer);
              setLoadingStatus("✍️ Summarizing results...");
              setLoading(false);
              if (event.clarifications?.length) setClarifications(event.clarifications);
              setMessages(prev => [...prev, {
                id:           msgId,
                role:         "assistant",
                content:      "",
                streaming:    true,
                tableData:    event.table_data    || [],
                tableColumns: event.table_columns || [],
                rowCount:     event.row_count     || 0,
                queryUsed:    event.query_used    || "",
                dataSource:   event.data_source   || "",
                queryType:    event.query_type    || "",
                isError:      false,
              }]);
            }
          }

          else if (event.event === "token") {
            // Always accumulate token in background buffer
            bgAccumulator.content += event.token;

            if (isStreamActive()) {
              clearInterval(statusTimer);
              setLoading(false);
              setLoadingStatus("");
              setMessages(prev => {
                const existingIdx = prev.findIndex(m => m.id === msgId);
                if (existingIdx >= 0) {
                  const updated = [...prev];
                  updated[existingIdx] = {
                    ...updated[existingIdx],
                    content:   updated[existingIdx].content + event.token,
                    streaming: true,
                  };
                  return updated;
                }
                return [...prev, {
                  id: msgId, role: "assistant", content: event.token,
                  streaming: true, tableData: [], tableColumns: [],
                  rowCount: 0, queryUsed: "", dataSource: "",
                  queryType: "document", isError: false,
                }];
              });
            }
          }

          else if (event.event === "done") {
            clearInterval(statusTimer);
            streamingSessionIdRef.current = null;  // stream finished — clear tracking

            // Always reconstruct from initialMessages rather than reading prev from setMessages.
            // If the user re-opens this session mid-stream, openSession calls setMessages([]),
            // which makes prev empty. Mapping over empty prev saves [] to DynamoDB and the
            // chat appears blank. bgAccumulator always holds the full accumulated content,
            // so reconstructing from initialMessages is correct in every case.
            const acc = bgAccumulator;
            const finalMessages = [
              ...initialMessages,
              { role: "user", content: msg },
              {
                id:           msgId,
                role:         "assistant",
                content:      acc.content,
                streaming:    false,
                tableData:    acc.tableData,
                tableColumns: acc.tableColumns,
                rowCount:     acc.rowCount,
                queryUsed:    acc.queryUsed,
                dataSource:   acc.dataSource,
                queryType:    acc.queryType || (acc.tableData.length ? "schema" : "document"),
                isError:      false,
              },
            ];
            if (sessionId) saveMessages(finalMessages, sessionId);

            if (isStreamActive()) {
              setLoading(false);
              setLoadingStatus("");
              setMessages(finalMessages);
              if (event.suggestions?.length) setSuggestions(event.suggestions);
            }
          }

          else if (event.event === "error") {
            clearInterval(statusTimer);
            if (isStreamActive()) {
              setLoading(false);
              setLoadingStatus("");
              if (event.clarifications?.length) {
                setClarifications(event.clarifications);
              } else {
                setMessages(prev => [...prev, {
                  role: "assistant",
                  content: event.message || "An error occurred.",
                  isError: true,
                }]);
              }
            }
          }
        },
        abortController.signal,
      );
    } catch (err) {
      clearInterval(statusTimer);
      streamingSessionIdRef.current = null;  // stream ended (abort or error) — clear tracking
      if (err.name === "AbortError") return;  // navigation away or new message sent — suppress error UI
      setLoading(false);
      setLoadingStatus("");
      setMessages(prev => [...prev, {
        role:    "assistant",
        content: `Error: ${err.message || "Failed to get response."}`,
        isError: true,
      }]);
    }
  };

  const handleThumbsUp = async (msg) => {
    if (!msg.id) return;
    const feedback = { rating: "positive" };
    const updated = messagesRef.current.map(m => m.id === msg.id ? { ...m, feedback } : m);
    setMessages(updated);
    if (currentSessionId) saveMessages(updated, currentSessionId);
    try {
      await boxerService.submitFeedback({
        session_id: currentSessionId || "",
        message_id: msg.id,
        rating: "positive",
        query_mode: queryModeObj.value,
        model_key: modelKey.value,
      });
    } catch {}
  };

  const openThumbsDownModal = (msg) => {
    if (!msg.id) return;
    setFeedbackModal({ visible: true, message: msg });
    setSelectedReasons([]);
    setFeedbackComment("");
  };

  const closeThumbsDownModal = () => {
    setFeedbackModal({ visible: false, message: null });
    setSelectedReasons([]);
    setFeedbackComment("");
  };

  const submitThumbsDown = async () => {
    const msg = feedbackModal.message;
    if (!msg) return;
    const feedback = { rating: "negative", reasons: selectedReasons, comment: feedbackComment };
    const updated = messagesRef.current.map(m => m.id === msg.id ? { ...m, feedback } : m);
    setMessages(updated);
    closeThumbsDownModal();
    if (currentSessionId) saveMessages(updated, currentSessionId);
    try {
      await boxerService.submitFeedback({
        session_id: currentSessionId || "",
        message_id: msg.id,
        rating: "negative",
        reasons: selectedReasons,
        comment: feedbackComment,
        query_mode: queryModeObj.value,
        model_key: modelKey.value,
      });
    } catch {}
  };

  const openCreatePromptModal = async () => {
    setCreatePromptModal(true);
    setDistilledPrompt("");
    setPromptName("");
    setDistillLoading(true);
    try {
      const history = messagesRef.current
        .filter(m => m.content && !m.isError)
        .map(m => ({ role: m.role, content: m.content }));
      const result = await boxerService.distillConversation(history);
      setDistilledPrompt(result);
      const firstUserMsg = messagesRef.current.find(m => m.role === "user");
      setPromptName((firstUserMsg?.content || "").slice(0, 60).trim());
    } catch {
      setDistilledPrompt("Failed to generate prompt. You can type one manually.");
    } finally {
      setDistillLoading(false);
    }
  };

  const closeCreatePromptModal = () => {
    setCreatePromptModal(false);
    setDistilledPrompt("");
    setPromptName("");
    setSavingPrompt(false);
    setDistillLoading(false);
  };

  const handleSaveDistilledPrompt = async () => {
    const name = promptName.trim();
    const content = distilledPrompt.trim();
    if (!name || !content) return;
    setSavingPrompt(true);
    await createSavedPrompt(name, content, queryModeObj.value);
    closeCreatePromptModal();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // True on Chrome/Edge; false on Firefox — mic button is hidden when false
  const speechSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Starts/stops voice recognition. Uses continuous mode so the browser never auto-stops mid-sentence.
  // A 2-second silence timer fires stop() after the user finishes speaking.
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      clearTimeout(silenceTimerRef.current);
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.lang = navigator.language || "en-US"; // uses OS/browser locale for accent-aware recognition
    rec.interimResults = true;  // fires onresult while speaking, not just at the end
    rec.continuous = true;      // keeps listening across pauses; we handle stop via silenceTimerRef
    rec.maxAlternatives = 1;
    rec.onstart  = () => setListening(true);
    rec.onend    = () => { clearTimeout(silenceTimerRef.current); setListening(false); setInterimText(""); };
    rec.onerror  = () => { clearTimeout(silenceTimerRef.current); setListening(false); setInterimText(""); };
    rec.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      if (interim) setInterimText(interim);
      if (final) {
        setInterimText("");
        clearTimeout(silenceTimerRef.current);
        const words = final.trim().split(/\s+/);
        const base  = inputValueRef.current;
        words.forEach((_, i) => {
          setTimeout(() => {
            const partial = words.slice(0, i + 1).join(" ");
            setInput(base ? `${base} ${partial}` : partial);
          }, i * 55); // 55ms per word gives a smooth streaming print effect
        });
        silenceTimerRef.current = setTimeout(() => recognitionRef.current?.stop(), 2000); // auto-stop after 2s silence
      }
    };
    recognitionRef.current = rec;
    rec.start();
  };

  return (
    <ContentLayout header={<Header variant="h1">🤖 Chat Bot</Header>}>
      <SpaceBetween size="m">
        <Box>
          Ask questions about your metadata in natural language. I'll convert your questions into queries and show you the results.
        </Box>
        <Box>
          This Unified Chat Assistant combines the strengths of DocuSense and OpsLense into a single, intelligent interface—designed to support both information discovery and operational decision-making.
        </Box>
        <Container>
          <ExpandableSection headerText="ℹ️ Overview">
            <div>
              <b>📘 What This Assistant Does:</b>
              <TextContent>
                <ul>
                  <li>Understands uploaded documents (policies, SOPs, reports, playbooks) and returns accurate, context-aware answers.</li>
                  <li>Applies operational intelligence to Warehouse Management and Supply Chain data—delivering expert-level insights traditionally associated with OpsLense.</li>
                  <li>Bridges documentation and live operational knowledge, eliminating the gap between “what is written” and “what is happening.”</li>
                  <li>
                    Instantly find the right data or information
                    <ul>
                      <li>Actual data results (counts, lists, filters)</li>
                      <li>Explanation and retuns most relevant answer</li>
                    </ul>
                  </li>
                </ul>
              </TextContent>
              <b>⚙️ Operational Intelligence Capabilities:</b>
              <TextContent>
                <ul>
                  <li>Invetory Management guidance</li>
                  <li>Supply Chain Optimization Insights</li>
                  <li>Context-Aware Recommendations</li>
                </ul>
              </TextContent>
              <b>🎯 Helps to:</b>
              <TextContent>
                <ul>
                  <li>Reduces dependency on analysts and tribal knowledge</li>
                  <li>Improves decision speed and confidence</li>
                  <li>Aligns documented processes with real-world execution</li>
                  <li>Enables consistent, scalable operational guidance</li>
                  <li>Acts as a single trusted source of operational truth</li>
                </ul>
              </TextContent>
            </div>
            <div style={{ borderTop: "1px solid var(--cds-color-border-default)", marginTop: 16, paddingTop: 16 }}>
              <ExpandableSection headerText="💡 Features">
                <TextContent>
                  <b>Core AI Modes</b>
                    <ul>
                        <li>OpsLense — Ask questions about live operational data in plain English. The AI queries your warehouse data (CSV/S3 tables) and returns structured results with a natural-language summary.</li>
                        <li>DocuSense — Ask questions about documents, SOPs, policies, or any knowledge base content. The AI retrieves relevant documents and answers in context.</li>
                    </ul>
                  <b>Chat Experience</b>
                    <ul>
                      <li>Streaming Responses — AI answers appear word-by-word in real time, so users see results as they're generated rather than waiting for the full response.</li>
                      <li>Data Table Results — For OpsLense queries, results are shown as a formatted data table alongside the AI summary — data and explanation together in one view.</li>
                      <li>Follow-up Suggestions — After every AI answer, the chatbot automatically suggests 3–4 relevant follow-up questions the user can click to continue the conversation.</li>
                      <li>Clarifying Questions — When a query is ambiguous, the AI asks the user to clarify rather than guessing — reducing wrong or misleading answers.</li>
                      <li>Voice Input — Users can speak their question using the microphone button in the chat input. Transcript appears live as they speak, then populates the input field.</li>
                    </ul>
                  <b>Session Management</b>
                    <ul>
                      <li>Chat History — Every conversation is automatically saved. Users can return to any previous session and continue where they left off.</li>
                      <li>Pinned Chats — Users can pin important conversations so they float to the top of the chat list for quick access.</li>
                      <li>Rename Sessions — Sessions can be given custom names for easy identification later.</li>
                    </ul>
                  <b>Saved Prompts</b>
                    <ul>
                      <li>Saved Prompt Templates — Users can save any prompt as a reusable template with a title. Useful for recurring queries they run regularly.</li>
                      <li>Distill to Prompt — The AI reads a full conversation and converts it into a clean, reusable prompt template with placeholder variables — turning a one-off chat into a repeatable workflow.</li>
                    </ul>
                  <b>Personalisation</b>
                    <ul>
                      <li>DocuSense Memory — DocuSense learns facts about the user over time (e.g. their role, DC, focus area) and uses that context in every future DocuSense conversation — answers become more relevant without the user having to repeat themselves.</li>
                      <li>Response Style Preferences — Users can control response length (Brief / Balanced / Detailed) and format (Bullets / Paragraphs) separately for OpsLense and DocuSense.</li>
                      <li>My Focus — A freetext field where users describe their role or area of responsibility (e.g. "I manage DC Joburg"). The AI uses this to scope every answer to what's relevant to them.</li>
                    </ul>
                  <b>Navigation & Productivity</b>
                    <ul>
                      <li>Command Palette (Ctrl+K) — A global quick-search overlay. Users can search across all their past chats, saved prompts, and app pages without leaving the current screen — keyboard-navigable.</li>
                      <li>Feedback Rating — Users can give a thumbs up or down on any AI response, with optional reasons and comments. Feedback is tracked in the admin analytics dashboard.</li>
                    </ul>
                </TextContent>
              </ExpandableSection>
            </div>
          </ExpandableSection>
        </Container>

        <Container>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}>
              <FormField label="Example Questions">
                <div style={{ gap: 8, flexWrap: "wrap", display: "flex" }}>
                  <Button variant="outline" href="../Assets/files/ChatBot - Example Questions.xlsx" target="_blank">
                    Chatbot Example Questions
                  </Button>
                  <Button variant="outline" href="../Assets/files/Shift Performance Questions.xlsx" target="_blank">
                    Shift Performance Questions
                  </Button>
                </div>
              </FormField>
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <FormField label="AI Model">
                <Select selectedOption={modelKey} onChange={handleModelChange} options={MODEL_OPTIONS} />
              </FormField>
            </div>
          </div>
        </Container>

        <Container>
          <div style={styles.chatWindow}>
            {messages.length === 0 ? (
              <div style={styles.empty}>
                <Box textAlign="center">
                  <div><img src={companylogo} alt="logo" style={{ height: "60px", marginBottom: 16 }} /></div>
                  <Box variant="h2">
                    {getGreeting()}, {user?.username}!
                  </Box>
                  <Box color="text-body-secondary" padding={{ top: "xs" }}>
                    {queryModeObj.value === "schema"
                      ? "Ask questions about operational data, tables, or metrics."
                      : "Ask about policies, SOPs, or company documents."}
                  </Box>
                </Box>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={msg.id || i}>
                  {msg.role === "user" && (
                    <div style={styles.userRow}>
                      <div style={styles.userBubble}>
                        <pre style={styles.messageText}>{msg.content}</pre>
                      </div>
                    </div>
                  )}
                  {msg.role === "assistant" && (
                    <div style={styles.assistantRow}>
                      <div style={msg.isError ? styles.errorCard : styles.assistantCard}>
                        {msg.isError ? (
                          <pre style={{ ...styles.messageText, color: "#d13212" }}>{msg.content}</pre>
                        ) : (
                          <>
                            <MarkdownRenderer text={msg.content} />
                            {!msg.streaming && msg.content && (
                              <Box padding={{ top: "xs" }}>
                                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
                                  {msg.feedback ? (
                                    <StatusIndicator type={msg.feedback.rating === "positive" ? "success" : "error"}>
                                      {msg.feedback.rating === "positive" ? "Helpful" : "Not helpful"}
                                    </StatusIndicator>
                                  ) : (
                                    <>
                                      <Button
                                        variant="icon"
                                        iconName="thumbs-up"
                                        ariaLabel="Helpful"
                                        onClick={() => handleThumbsUp(msg)}
                                      />
                                      <Button
                                        variant="icon"
                                        iconName="thumbs-down"
                                        ariaLabel="Not helpful"
                                        onClick={() => openThumbsDownModal(msg)}
                                      />
                                    </>
                                  )}
                                  <CopyToClipboard
                                    copyButtonAriaLabel="Copy response"
                                    copyErrorText="Failed to copy"
                                    copySuccessText="Copied!"
                                    textToCopy={msg.content}
                                    variant="icon"
                                  />
                                </div>
                              </Box>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {msg.role === "assistant" && !msg.isError && msg.tableData?.length > 0 && (
                    <div style={styles.tableWrapper}>
                      {msg.tableDataTruncated && (
                        <Box color="text-body-secondary" fontSize="body-s" padding={{ bottom: "xs" }}>
                          Showing first 50 rows (saved view).
                        </Box>
                      )}
                      <DataTable columns={msg.tableColumns} data={msg.tableData} rowCount={msg.rowCount} />
                    </div>
                  )}
                </div>
              ))
            )}

            {loading && (
              <div style={styles.assistantRow}>
                <div style={styles.loadingCard}>
                  <Spinner size="normal" />&nbsp;&nbsp;{loadingStatus || "Processing your query..."}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {clarifications.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Box fontWeight="bold" padding={{ bottom: "xs" }}>💭 Did you mean one of these?</Box>
              <div style={styles.suggestionsGrid}>
                {clarifications.map((c, i) => (
                  <div key={i} style={styles.suggestionCell}>
                    <Button variant="normal" onClick={() => { setClarifications([]); sendMessage(c.suggestion); }}>
                      {c.suggestion}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {suggestions.length > 0 && messages.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Box fontWeight="bold" padding={{ bottom: "xs" }}>💡 Suggested Questions</Box>
              <div style={styles.suggestionsGrid}>
                {suggestions.map((s, i) => (
                  <div key={i} style={styles.suggestionCell}>
                    <Button variant="normal" onClick={() => { setSuggestions([]); sendMessage(s); }}>
                      {s}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const completedAiCount = messages.filter(m => m.role === "assistant" && !m.streaming && !m.isError && m.content).length;
            return completedAiCount >= 2 ? (
              <div style={{ display: "flex", justifyContent: "center", paddingBottom: 4, marginTop: 12 }}>
                <Button
                  variant="normal"
                  iconName="star"
                  onClick={openCreatePromptModal}
                  disabled={loading}
                  title="Let AI analyze this conversation and create a reusable prompt that you can use again or share with teammates."
                >
                  Create Reusable Prompt
                </Button>
              </div>
            ) : null;
          })()}

          <div style={styles.modeSwitcherRow}>
            <ModeSwitcher modes={QUERY_MODES} value={queryModeObj} onChange={handleModeChange} />
          </div>

          <div style={styles.inputRow}>
            <div style={styles.textareaWrapper}>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={queryModeObj.value === "schema"
                  ? "Ask about operational data, tables, or metrics..."
                  : "Ask about policies, SOPs, or company documents..."}
                style={{ ...styles.textarea, paddingRight: speechSupported ? 44 : 12 }}
                rows={2}
                disabled={loading}
              />
              {speechSupported && (
                <div style={styles.micWrapper}>
                  <Button
                    variant="icon"
                    iconName={listening ? "microphone-off" : "microphone"}
                    ariaLabel={listening ? "Stop recording" : "Start voice input"}
                    onClick={toggleVoice}
                    disabled={loading}
                  />
                  {listening && <div style={styles.micPulse} />}
                </div>
              )}
              {listening && interimText && (
                <div style={styles.interimPreview}>{interimText}</div>
              )}
            </div>
            <Button
              variant="primary"
              onClick={() => sendMessage()}
              loading={loading}
              disabled={!input.trim()}
              iconName="send"
            >
              Send
            </Button>
          </div>
        </Container>

      <Modal
        visible={feedbackModal.visible}
        onDismiss={closeThumbsDownModal}
        header="What went wrong?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={closeThumbsDownModal}>Cancel</Button>
              <Button variant="primary" onClick={submitThumbsDown}>Submit</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box color="text-body-secondary">Select all that apply:</Box>
          {THUMBS_DOWN_REASONS.map(reason => (
            <Checkbox
              key={reason}
              checked={selectedReasons.includes(reason)}
              onChange={({ detail }) =>
                setSelectedReasons(prev =>
                  detail.checked ? [...prev, reason] : prev.filter(r => r !== reason)
                )
              }
            >
              {reason}
            </Checkbox>
          ))}
          <FormField label="Additional comments (optional)">
            <Textarea
              value={feedbackComment}
              onChange={({ detail }) => setFeedbackComment(detail.value)}
              placeholder="Tell us more about the issue..."
              rows={3}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={createPromptModal}
        onDismiss={closeCreatePromptModal}
        header="✨ Create Reusable Prompt"
        footer={
          !distillLoading && (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={closeCreatePromptModal}>Cancel</Button>
                <Button
                  variant="primary"
                  loading={savingPrompt}
                  disabled={!promptName.trim() || !distilledPrompt.trim()}
                  onClick={handleSaveDistilledPrompt}
                >
                  Save Prompt
                </Button>
              </SpaceBetween>
            </Box>
          )
        }
      >
        {distillLoading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "24px 0" }}>
            <Spinner size="large" />
            <Box color="text-body-secondary">
              Analysing your conversation and generating a reusable prompt...
            </Box>
          </div>
        ) : (
          <SpaceBetween size="m">
            <Box color="text-body-secondary">
              AI has distilled your conversation into a single reusable prompt. Edit it if needed, then save.
            </Box>
            <FormField label="Generated prompt (editable)">
              <Textarea
                value={distilledPrompt}
                onChange={({ detail }) => setDistilledPrompt(detail.value)}
                placeholder="AI-generated prompt will appear here..."
                rows={6}
              />
            </FormField>
            <FormField label="Prompt name">
              <Textarea
                value={promptName}
                onChange={({ detail }) => setPromptName(detail.value)}
                placeholder="Give this prompt a short name..."
                rows={2}
              />
            </FormField>
          </SpaceBetween>
        )}
      </Modal>

      </SpaceBetween>
    </ContentLayout>
  );
}

const styles = {
  chatWindow:      { flex: 1, overflowY: "auto", padding: "8px 0", display: "flex", flexDirection: "column", gap: 10 },
  empty:           { display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: 100, padding: 24 },
  userRow:         { display: "flex", justifyContent: "flex-end", marginTop: 8 },
  userBubble:      { background: "#0972d3", color: "#fff", borderRadius: "16px 16px 4px 16px", padding: "10px 16px", maxWidth: "60%", width: "fit-content" },
  assistantRow:    { display: "flex", justifyContent: "flex-start", marginTop: 8 },
  assistantCard:   { background: "#ffffff", border: "1px solid #e3e8ef", borderRadius: "4px 16px 16px 16px", padding: "16px 20px", width: "100%", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  errorCard:       { background: "#fdf3f1", border: "1px solid #d13212", borderRadius: "4px 16px 16px 16px", padding: "12px 16px", width: "100%", color: "#d13212" },
  loadingCard:     { background: "#f8f9fb", border: "1px solid #e3e8ef", borderRadius: "4px 16px 16px 16px", padding: "12px 16px", display: "flex", alignItems: "center", color: "#5f6b7a", fontSize: 14 },
  messageText:     { whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0, fontSize: 14, lineHeight: 1.6 },
  tableWrapper:    { margin: "8px 0" },
  suggestionsGrid: { display: "grid", gap: 5, gridTemplateColumns: "1fr 1fr", width: "100%" },
  suggestionCell:  { display: "grid", width: "100%" },
  modeSwitcherRow: { display: "flex", justifyContent: "center", padding: "10px 16px 4px", borderTop: "1px solid #eee", marginTop: 12 },
  inputRow:        { display: "flex", gap: 8, alignItems: "flex-end", borderTop: "1px solid #eee", paddingTop: 12 },
  textareaWrapper: { flex: 1, position: "relative" },
  textarea:        { width: "100%", boxSizing: "border-box", resize: "none", border: "1px solid #ccc", borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", lineHeight: 1.5 },
  micWrapper:      { position: "absolute", bottom: 6, right: 6, display: "flex", alignItems: "center", justifyContent: "center" },
  micPulse:        { position: "absolute", width: 32, height: 32, borderRadius: "50%", background: "rgba(229,57,53,0.18)", animation: "micPulse 1s ease-in-out infinite", pointerEvents: "none" },
  interimPreview:  { fontSize: 12, color: "#687078", fontStyle: "italic", padding: "3px 12px 0" },
};
